import { Injectable, inject } from '@angular/core'
import { Observable, catchError, from, of, switchMap, tap, map } from 'rxjs'
import { BaseAsyncService } from '@core/state/base-service'
import { ApiResponse, UserProfile, UserProfileDto, mapUserDtoToProfile } from '@shared/models/user/user.models'
import { UpdateAvatarRequest } from '@shared/models/dto/api-dtos'
import { ProfileStore } from '@core/state/profile.store'

@Injectable({ providedIn: 'root' })
export class UserService extends BaseAsyncService {
  private readonly profileStore = inject(ProfileStore)

  /**
   * Loads current user by id and stores it in ProfileStore
   * - Returns the mapped UserProfile on success
   * - Returns null on error and updates store error state
   */

  updateAvatar(userId: string | number, avatarUrl: string): Observable<void> {
    const body: UpdateAvatarRequest = { avatarUrl }
    return this.put<void>(`/users/${userId}/avatar`, body)
  }

  uploadAvatar(userId: string | number, file: File): Observable<UpdateAvatarRequest> {
    const formData = new FormData()
    formData.append('file', file)
    return this.post<UpdateAvatarRequest>(`/users/${userId}/avatar/upload`, formData)
  }

  getAvatar(userId: string | number): Observable<string | null> {
    return this.getBlob(`/users/${userId}/avatar`).pipe(
      switchMap(blob => {
        if (!blob || blob.size === 0) return of(null)

        if (blob.type.startsWith('image/')) {
          return new Observable<string>(observer => {
            const reader = new FileReader()
            reader.onload = () => { observer.next(reader.result as string); observer.complete() }
            reader.onerror = () => observer.error(reader.error)
            reader.readAsDataURL(blob)
          })
        }

        return from(blob.text()).pipe(
          map(text => {
            try {
              const parsed = JSON.parse(text)
              return parsed?.data?.avatarUrl ?? parsed?.avatarUrl ?? null
            } catch {
              return text.trim() || null
            }
          })
        )
      }),
      catchError(() => of(null))
    )
  }

  loadUser(userId: string): Observable<UserProfile | null> {
    this.profileStore.setLoading(true)
    this.profileStore.setError(null)

    return this.get<ApiResponse<UserProfileDto>>(`/users/${userId}`, {
      logErrors: true,
      retry: this.createRetryConfig(0, 800, 1.5)
    }).pipe(
      map((resp) => {
        const profile = mapUserDtoToProfile(resp.data)
        this.profileStore.setProfile(profile)
        return profile
      }),
      catchError(() => {
        this.profileStore.setError('Impossible de charger le profil utilisateur')
        return of(null)
      }),
      tap(() => this.profileStore.setLoading(false))
    )
  }
}


